/**
 * Central education system registry.
 *
 * DB/API values use stable ids. UI should display labels from this file.
 */

import type { EducationLevel, EducationSystem, LegacyEducationLevel } from './enums';

export type EducationSystemId = EducationSystem;
export type EducationLevelId = Exclude<EducationLevel, LegacyEducationLevel>;
export type SubjectId = string;

export interface SubjectDefinition {
  id: SubjectId;
  label: string;
}

export interface EducationLevelDefinition {
  value: EducationLevel;
  id: EducationLevelId;
  systemId: EducationSystemId;
  label: string;
  subjects: string[];
  subjectOptions: SubjectDefinition[];
  dashboardLabel: string;
  dashboardColor: string;
}

export interface EducationSystemDefinition {
  id: EducationSystemId;
  label: string;
  levels: Array<{
    id: EducationLevelId;
    label: string;
    subjects: string[];
  }>;
}

export interface LevelDisplayOptions {
  includeSystem?: boolean;
  useShortSystemLabel?: boolean;
}

export interface LevelGroupMetadata {
  systemId: EducationSystemId;
  label: string;
  startLabel: string;
  endLabel: string;
  color: string;
}

const slug = (value: string): string =>
  value
    .toLowerCase()
    .replace(/&/g, ' and ')
    .replace(/[^a-z0-9]+/g, '_')
    .replace(/^_+|_+$/g, '');

const subjects = (items: string[]): SubjectDefinition[] =>
  items.map((label) => ({ id: slug(label), label }));

const nationalLowerPrimary = [
  'Malay',
  'English',
  'Chinese',
  'Tamil',
  'Math',
  'Science',
  'Moral',
  'Islamic Studies',
  'Art',
  'Music',
];

const nationalUpperPrimary = [
  'Malay',
  'English',
  'Chinese',
  'Tamil',
  'Math',
  'Science',
  'History',
  'Moral',
  'Islamic Studies',
  'RBT',
  'Art',
  'Music',
];

const nationalLowerSecondary = [
  'Malay',
  'English',
  'Chinese',
  'Tamil',
  'Arabic',
  'Math',
  'Science',
  'History',
  'Geography',
  'Moral',
  'Islamic Studies',
  'RBT',
  'ASK',
  'Art',
  'Music',
];

const nationalUpperSecondary = [
  'Malay',
  'English',
  'Math',
  'History',
  'Science',
  'Moral',
  'Islamic Studies',
  'Add Math',
  'Physics',
  'Chemistry',
  'Biology',
  'Computer Science',
  'Accounting',
  'Business',
  'Economics',
  'Geography',
  'Chinese',
  'Tamil',
  'Arabic',
  'Art',
];

const nationalForm6 = [
  'General Studies',
  'Malay',
  'English',
  'MUET',
  'Math T',
  'Math M',
  'Further Math',
  'Physics',
  'Chemistry',
  'Biology',
  'ICT',
  'Economics',
  'Business',
  'Accounting',
  'History',
  'Geography',
  'Chinese',
  'Tamil',
];

const cisLower = ['Chinese', 'Malay', 'English', 'Math', 'Science', 'History', 'Geography', 'Art', 'ICT'];
const cisUpper = [
  'Chinese',
  'Malay',
  'English',
  'Math',
  'Add Math',
  'Add Math I',
  'Add Math II',
  'Physics',
  'Chemistry',
  'Biology',
  'History',
  'Geography',
  'Accounting',
  'Business',
  'Economics',
  'ICT',
  'Art',
];

const internationalPrimary = [
  'English',
  'Mandarin',
  'Malay',
  'Math',
  'Science',
  'Global Perspectives',
  'ICT',
  'Art',
  'Music',
];

const internationalLower = [
  'English',
  'Mandarin',
  'Malay',
  'Math',
  'Science',
  'History',
  'Geography',
  'Global Perspectives',
  'ICT',
  'Art',
  'Music',
];

const internationalUpper = [
  'English/EFL',
  'English/ESL',
  'Math',
  'Add Math',
  'Physics',
  'Chemistry',
  'Biology',
  'Coordinated Science',
  'Computer Science',
  'ICT',
  'Business',
  'Economics',
  'Accounting',
  'History',
  'Geography',
  'Global Perspectives',
  'Mandarin',
  'Malay',
  'Literature',
  'Art & Design',
  'Music',
];

export const EDUCATION_SYSTEM_DEFINITIONS: EducationSystemDefinition[] = [
  {
    id: 'national_school',
    label: 'National School / SPM',
    levels: [
      { id: 'national_standard_1', label: 'Standard 1', subjects: nationalLowerPrimary },
      { id: 'national_standard_2', label: 'Standard 2', subjects: nationalLowerPrimary },
      { id: 'national_standard_3', label: 'Standard 3', subjects: nationalLowerPrimary },
      { id: 'national_standard_4', label: 'Standard 4', subjects: nationalUpperPrimary },
      { id: 'national_standard_5', label: 'Standard 5', subjects: nationalUpperPrimary },
      { id: 'national_standard_6', label: 'Standard 6', subjects: nationalUpperPrimary },
      { id: 'national_form_1', label: 'Form 1', subjects: nationalLowerSecondary },
      { id: 'national_form_2', label: 'Form 2', subjects: nationalLowerSecondary },
      { id: 'national_form_3', label: 'Form 3', subjects: nationalLowerSecondary },
      { id: 'national_form_4', label: 'Form 4', subjects: nationalUpperSecondary },
      { id: 'national_form_5', label: 'Form 5', subjects: nationalUpperSecondary },
      { id: 'national_form_6', label: 'Form 6', subjects: nationalForm6 },
    ],
  },
  {
    id: 'chinese_independent_school',
    label: 'Independent Chinese School / UEC',
    levels: [
      { id: 'cis_form_1', label: 'Form 1', subjects: cisLower },
      { id: 'cis_form_2', label: 'Form 2', subjects: cisLower },
      { id: 'cis_form_3', label: 'Form 3', subjects: cisLower },
      { id: 'cis_form_4', label: 'Form 4', subjects: cisUpper },
      { id: 'cis_form_5', label: 'Form 5', subjects: cisUpper },
      { id: 'cis_form_6', label: 'Form 6', subjects: cisUpper },
    ],
  },
  {
    id: 'international_school',
    label: 'International School',
    levels: [
      { id: 'international_year_1', label: 'Year 1', subjects: internationalPrimary },
      { id: 'international_year_2', label: 'Year 2', subjects: internationalPrimary },
      { id: 'international_year_3', label: 'Year 3', subjects: internationalPrimary },
      { id: 'international_year_4', label: 'Year 4', subjects: internationalPrimary },
      { id: 'international_year_5', label: 'Year 5', subjects: internationalPrimary },
      { id: 'international_year_6', label: 'Year 6', subjects: internationalPrimary },
      { id: 'international_year_7', label: 'Year 7', subjects: internationalLower },
      { id: 'international_year_8', label: 'Year 8', subjects: internationalLower },
      { id: 'international_year_9', label: 'Year 9', subjects: internationalLower },
      { id: 'international_year_10', label: 'Year 10', subjects: internationalUpper },
      { id: 'international_year_11', label: 'Year 11', subjects: internationalUpper },
    ],
  },
];

export const LEGACY_LEVEL_MAP: Record<LegacyEducationLevel, EducationLevelId> = {
  primary_1: 'national_standard_1',
  primary_2: 'national_standard_2',
  primary_3: 'national_standard_3',
  primary_4: 'national_standard_4',
  primary_5: 'national_standard_5',
  primary_6: 'national_standard_6',
  form_1: 'national_form_1',
  form_2: 'national_form_2',
  form_3: 'national_form_3',
  form_4: 'national_form_4',
  form_5: 'national_form_5',
  form_6: 'national_form_6',
};

export const EDUCATION_SYSTEM_OPTIONS = EDUCATION_SYSTEM_DEFINITIONS.map(({ id, label }) => ({
  value: id,
  label,
}));

export const EDUCATION_SYSTEM_SHORT_LABELS: Record<EducationSystemId, string> = {
  national_school: 'National',
  chinese_independent_school: 'CIS',
  international_school: 'International',
};

const dashboardColorBySystem: Record<EducationSystemId, string> = {
  national_school: 'bg-[var(--color-primary)]',
  chinese_independent_school: 'bg-[var(--color-secondary)]',
  international_school: 'bg-[var(--color-accent)]',
};

export const EDUCATION_LEVEL_DEFINITIONS: EducationLevelDefinition[] = EDUCATION_SYSTEM_DEFINITIONS.flatMap(
  (system) =>
    system.levels.map((level) => ({
      value: level.id,
      id: level.id,
      systemId: system.id,
      label: level.label,
      subjects: level.subjects,
      subjectOptions: subjects(level.subjects),
      dashboardLabel: `${system.label} - ${level.label}`,
      dashboardColor: dashboardColorBySystem[system.id],
    }))
);

const byLevelId = new Map<string, EducationLevelDefinition>(
  EDUCATION_LEVEL_DEFINITIONS.map((definition) => [definition.id, definition])
);

const legacyDefinitionEntries = Object.entries(LEGACY_LEVEL_MAP).map(([legacy, current]) => {
  const definition = byLevelId.get(current)!;
  return [legacy, definition] as const;
});

export const EDUCATION_LEVEL_OPTIONS = EDUCATION_LEVEL_DEFINITIONS.map(({ value, label, systemId }) => ({
  value,
  label,
  systemId,
}));

export const EDUCATION_LEVEL_NEW_OPTIONS = EDUCATION_LEVEL_OPTIONS;

export const EDUCATION_LEVEL_VALUES = EDUCATION_LEVEL_DEFINITIONS.map((definition) => definition.id);

export const EDUCATION_LEVEL_LABELS: Record<string, string> = {
  ...Object.fromEntries(EDUCATION_LEVEL_DEFINITIONS.map(({ id, label }) => [id, label])),
  ...Object.fromEntries(legacyDefinitionEntries.map(([legacy, definition]) => [legacy, definition.label])),
};

export const LEVEL_SUBJECTS: Record<string, string[]> = {
  ...Object.fromEntries(EDUCATION_LEVEL_DEFINITIONS.map(({ id, subjects }) => [id, subjects])),
  ...Object.fromEntries(legacyDefinitionEntries.map(([legacy, definition]) => [legacy, definition.subjects])),
};

export const DASHBOARD_EDUCATION_LABELS: Record<string, string> = {
  ...Object.fromEntries(EDUCATION_LEVEL_DEFINITIONS.map(({ id, dashboardLabel }) => [id, dashboardLabel])),
  ...Object.fromEntries(legacyDefinitionEntries.map(([legacy, definition]) => [legacy, definition.dashboardLabel])),
};

export const DASHBOARD_EDUCATION_COLORS: Record<string, string> = {
  ...Object.fromEntries(EDUCATION_LEVEL_DEFINITIONS.map(({ id, dashboardColor }) => [id, dashboardColor])),
  ...Object.fromEntries(legacyDefinitionEntries.map(([legacy, definition]) => [legacy, definition.dashboardColor])),
};

export function normalizeEducationLevel(value: EducationLevel | string): EducationLevelId | string {
  return LEGACY_LEVEL_MAP[value as LegacyEducationLevel] ?? value;
}

export function getEducationSystemDefinition(
  systemId: EducationSystemId | string
): EducationSystemDefinition | undefined {
  return EDUCATION_SYSTEM_DEFINITIONS.find((system) => system.id === systemId);
}

export function getEducationLevelDefinition(
  value: EducationLevel | string
): EducationLevelDefinition | undefined {
  return byLevelId.get(normalizeEducationLevel(value)) ?? undefined;
}

export function getEducationLevelsForSystem(systemId: EducationSystemId | string) {
  return EDUCATION_LEVEL_DEFINITIONS.filter((definition) => definition.systemId === systemId);
}

export function getSystemForLevel(value: EducationLevel | string): EducationSystemId | undefined {
  return getEducationLevelDefinition(value)?.systemId;
}

export function getSubjectsForLevel(value: EducationLevel | string): string[] {
  return getEducationLevelDefinition(value)?.subjects ?? [];
}

export function getSubjectOptionsForLevel(value: EducationLevel | string): SubjectDefinition[] {
  return getEducationLevelDefinition(value)?.subjectOptions ?? [];
}

export function getLevelLabel(value: EducationLevel | string): string {
  return EDUCATION_LEVEL_LABELS[value] ?? EDUCATION_LEVEL_LABELS[normalizeEducationLevel(value)] ?? value;
}

export function getEducationSystemLabel(value: EducationSystemId | string): string {
  return getEducationSystemDefinition(value)?.label ?? value;
}

export function getEducationSystemShortLabel(value: EducationSystemId | string): string {
  return EDUCATION_SYSTEM_SHORT_LABELS[value as EducationSystemId] ?? getEducationSystemLabel(value);
}

export function getSubjectLabel(subjectIdOrLabel: string): string {
  for (const level of EDUCATION_LEVEL_DEFINITIONS) {
    const subject = level.subjectOptions.find((item) => item.id === subjectIdOrLabel || item.label === subjectIdOrLabel);
    if (subject) return subject.label;
  }
  return subjectIdOrLabel;
}

export function getLevelDashboardColor(value: EducationLevel | string): string {
  return DASHBOARD_EDUCATION_COLORS[value] ?? DASHBOARD_EDUCATION_COLORS[normalizeEducationLevel(value)] ?? 'bg-gray-500';
}

export function isLegacyLevel(value: EducationLevel | string): boolean {
  return value in LEGACY_LEVEL_MAP;
}

export function isNewGranularLevel(value: EducationLevel | string): boolean {
  return Boolean(byLevelId.get(value)) && !isLegacyLevel(value);
}

export const LEVEL_GROUPS: Record<string, EducationLevel[]> = {
  national_primary_lower: ['national_standard_1', 'national_standard_2', 'national_standard_3'],
  national_primary_upper: ['national_standard_4', 'national_standard_5', 'national_standard_6'],
  national_lower_secondary: ['national_form_1', 'national_form_2', 'national_form_3'],
  national_upper_secondary: ['national_form_4', 'national_form_5'],
  national_form_6: ['national_form_6'],
  cis_lower_secondary: ['cis_form_1', 'cis_form_2', 'cis_form_3'],
  cis_upper_secondary: ['cis_form_4', 'cis_form_5', 'cis_form_6'],
  international_primary: [
    'international_year_1',
    'international_year_2',
    'international_year_3',
    'international_year_4',
    'international_year_5',
    'international_year_6',
  ],
  international_lower_secondary: ['international_year_7', 'international_year_8', 'international_year_9'],
  international_upper_secondary: ['international_year_10', 'international_year_11'],
  primary_lower: ['primary_1', 'primary_2', 'primary_3'],
  primary_upper: ['primary_4', 'primary_5', 'primary_6'],
  lower_secondary: ['form_1', 'form_2', 'form_3'],
  upper_secondary: ['form_4', 'form_5'],
  form_6: ['form_6'],
};

export const LEVEL_GROUP_METADATA: Record<string, LevelGroupMetadata> = {
  national_primary_lower: {
    systemId: 'national_school',
    label: 'Standard 1-3',
    startLabel: 'Standard 1',
    endLabel: 'Standard 3',
    color: 'bg-sky-500',
  },
  national_primary_upper: {
    systemId: 'national_school',
    label: 'Standard 4-6',
    startLabel: 'Standard 4',
    endLabel: 'Standard 6',
    color: 'bg-cyan-500',
  },
  national_lower_secondary: {
    systemId: 'national_school',
    label: 'Form 1-3',
    startLabel: 'Form 1',
    endLabel: 'Form 3',
    color: 'bg-violet-500',
  },
  national_upper_secondary: {
    systemId: 'national_school',
    label: 'Form 4-5',
    startLabel: 'Form 4',
    endLabel: 'Form 5',
    color: 'bg-amber-500',
  },
  national_form_6: {
    systemId: 'national_school',
    label: 'Form 6',
    startLabel: 'Form 6',
    endLabel: 'Form 6',
    color: 'bg-rose-500',
  },
  cis_lower_secondary: {
    systemId: 'chinese_independent_school',
    label: 'Form 1-3',
    startLabel: 'Form 1',
    endLabel: 'Form 3',
    color: 'bg-emerald-500',
  },
  cis_upper_secondary: {
    systemId: 'chinese_independent_school',
    label: 'Form 4-6',
    startLabel: 'Form 4',
    endLabel: 'Form 6',
    color: 'bg-teal-500',
  },
  international_primary: {
    systemId: 'international_school',
    label: 'Year 1-6',
    startLabel: 'Year 1',
    endLabel: 'Year 6',
    color: 'bg-fuchsia-500',
  },
  international_lower_secondary: {
    systemId: 'international_school',
    label: 'Year 7-9',
    startLabel: 'Year 7',
    endLabel: 'Year 9',
    color: 'bg-indigo-500',
  },
  international_upper_secondary: {
    systemId: 'international_school',
    label: 'Year 10-11',
    startLabel: 'Year 10',
    endLabel: 'Year 11',
    color: 'bg-orange-500',
  },
};

export const LEVEL_GROUP_ORDER = Object.keys(LEVEL_GROUP_METADATA);

export function getLevelGroup(level: EducationLevel): EducationLevel[] {
  const normalized = normalizeEducationLevel(level) as EducationLevel;
  return Object.values(LEVEL_GROUPS).find((group) => group.includes(normalized)) ?? [normalized];
}

export function getLevelGroupKey(value: EducationLevel | string): string | undefined {
  const normalized = normalizeEducationLevel(value) as EducationLevel;

  for (const groupKey of LEVEL_GROUP_ORDER) {
    if (LEVEL_GROUPS[groupKey]?.includes(normalized)) {
      return groupKey;
    }
  }

  return undefined;
}

export function formatEducationLevel(
  value: EducationLevel | string,
  options: LevelDisplayOptions = {}
): string {
  const levelLabel = getLevelLabel(value);

  if (!options.includeSystem) {
    return levelLabel;
  }

  const systemId = getSystemForLevel(value);
  if (!systemId) {
    return levelLabel;
  }

  const systemLabel = options.useShortSystemLabel
    ? getEducationSystemShortLabel(systemId)
    : getEducationSystemLabel(systemId);

  return `${systemLabel} · ${levelLabel}`;
}

export function formatEducationSystemLevel(
  systemId: EducationSystemId | string | undefined,
  level: EducationLevel | string,
  options: Omit<LevelDisplayOptions, 'includeSystem'> = {}
): string {
  const levelLabel = getLevelLabel(level);
  const resolvedSystemId = systemId ?? getSystemForLevel(level);

  if (!resolvedSystemId) {
    return levelLabel;
  }

  const systemLabel = options.useShortSystemLabel
    ? getEducationSystemShortLabel(resolvedSystemId)
    : getEducationSystemLabel(resolvedSystemId);

  return `${systemLabel} · ${levelLabel}`;
}

export function getSubjectsForLevels(levels: Array<EducationLevel | string>): string[] {
  return [...new Set(levels.flatMap((level) => getSubjectsForLevel(level)))];
}

export function isValidEducationSystem(value: string): value is EducationSystemId {
  return EDUCATION_SYSTEM_DEFINITIONS.some((system) => system.id === value);
}

export function isValidEducationLevel(value: string): boolean {
  return Boolean(getEducationLevelDefinition(value));
}

export function isValidSubjectForLevel(level: string, subject: string): boolean {
  const definition = getEducationLevelDefinition(level);
  if (!definition) return false;
  return definition.subjectOptions.some((item) => item.id === subject || item.label === subject);
}

export function isValidLevelForSystem(systemId: string, level: string): boolean {
  return getEducationLevelDefinition(level)?.systemId === systemId;
}
